import {TKey, useTranslation} from '@/translations/i18n'
import {useFormContext} from 'react-hook-form'
import {CreateViewSchema} from '../../formSchemas'
import {View, ViewEntity} from '../../types'
import {InputText} from '@/components/commons/input-text/InputText'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {DialogBody, DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {StyledFooter} from './style'

const getEntityLabel = {
    [ViewEntity.enum.users]: 'commons:users',
    [ViewEntity.enum.patients]: 'commons:patients',
    [ViewEntity.enum.treatmentRequestsAll]: 'treatment_requests:title',
    [ViewEntity.enum.treatmentRequestsWaitingList]: 'treatment_requests:title',
    [ViewEntity.enum.treatmentRequestsInProgress]: 'treatment_requests:title',
    [ViewEntity.enum.treatmentRequestsAccepted]: 'treatment_requests:title',
    [ViewEntity.enum.treatmentRequestsRejected]: 'treatment_requests:title',
    [ViewEntity.enum.treatmentRequestsInReview]: 'treatment_requests:title',
    [ViewEntity.enum.projects]: 'projects:title',
    [ViewEntity.enum.accesses]: 'accesses:title'
} as const satisfies Record<ViewEntity, TKey>

export const ViewForm = ({
    onSubmit,
    isPending,
    submitLabel,
    entity
}: {
    onSubmit: (data: CreateViewSchema) => void
    isPending: boolean
    submitLabel: string
    entity: ViewEntity
}) => {
    const {t} = useTranslation()
    const {
        handleSubmit,
        register,
        formState: {isValid, errors, dirtyFields}
    } = useFormContext<CreateViewSchema>()
    const isDirty = Object.keys(dirtyFields).length >= 1

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogBody>
                <InputText
                    label={t('commons:first_name_label')}
                    type="text"
                    placeholder={t('commons:enter_name')}
                    {...register('name')}
                    errorMessage={errors.name?.message}
                />
                <Flexbox direction="column" gap={2.5}>
                    <Label>{t('commons:type')}</Label>
                    <Flexbox direction="column" gap={3}>
                        <Flexbox>
                            <RadioField
                                label=""
                                value={View.shape.visibility.enum.private}
                                {...register('visibility')}
                            />
                            <Flexbox direction="column">
                                <Text size="textMd" palette="neutral" shade="600" weight="500">
                                    {t('commons:private_view')}
                                </Text>
                                <Text size="textMd" palette="neutral" shade="600">
                                    {t('commons:private_view_description')}
                                </Text>
                            </Flexbox>
                        </Flexbox>
                        <Flexbox>
                            <RadioField
                                label=""
                                value={View.shape.visibility.enum.public}
                                {...register('visibility')}
                            />
                            <Flexbox direction="column">
                                <Text size="textMd" palette="neutral" shade="600" weight="500">
                                    {t('commons:public_view')}
                                </Text>
                                <Text size="textMd" palette="neutral" shade="600">
                                    {t('commons:public_view_description', {entity: t(getEntityLabel[entity])})}
                                </Text>
                            </Flexbox>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </DialogBody>
            <StyledFooter>
                <DialogDismiss render={<Button variant="secondary" />} disabled={isPending}>
                    {t('commons:cancel')}
                </DialogDismiss>
                <Button variant="primaryNeutral" disabled={!isValid || isPending || !isDirty}>
                    {submitLabel}
                    {isPending && <Spinner size={12} />}
                </Button>
            </StyledFooter>
        </form>
    )
}
